import Spinner from '@/components/atoms/Spinner'
import { CardCourse } from '@/components/molecules/Admin/Cards/CardCourse'
import cohortsService from '@/services/cohortsService'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, Navigation } from 'swiper/modules'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import utc from 'dayjs/plugin/utc.js'
import Icon from '@/components/atoms/Icon'
import { twMerge } from 'tailwind-merge'
import classNames from 'classnames'
import { useAppDispatch } from '@/store/hooks'
import { matriculateCreators } from '@/store/ducks/matriculate'

dayjs.extend(utc)
dayjs.locale('pt-br')

interface Course {
	id: number
	name: string
	curso_id: number
	progress: string
	capa_card: string
	status: string
}

export function MatriculateCourses() {
	const { data: cohorts, isLoading } = cohortsService.getCohortsUser()
	const dispatch = useAppDispatch()

	const handleCohortOpen = (course: Course) => {
		dispatch(matriculateCreators.setClassSelect(course))
	}

	return (
		<div className='relative flex w-full flex-col gap-8'>
			<div className='flex'>
				<p className='text-zinc-50 text-3xl leading-8 font-semibold w-full'>
					Seus Cursos
				</p>

				<div
					className={twMerge(
						classNames('hidden lg:flex w-full gap-4 justify-end', {
							'!hidden': !cohorts?.courses?.length,
						})
					)}
				>
					<div
						className={`cursor-pointer swiper-button-prev-my-courses relative flex items-center justify-center after:hidden w-10 h-10 bg-brand-primary rounded-full z-10 pointer-events-auto`}
					>
						<Icon
							name='slick-arrow-right'
							width='24'
							height='24'
							className='w-[24px] h-[24px] rotate-180'
						/>
					</div>

					<div
						className={`cursor-pointer swiper-button-next-my-courses relative flex items-center justify-center after:hidden w-10 h-10 bg-brand-primary rounded-full z-10 pointer-events-auto`}
					>
						<Icon
							name='slick-arrow-right'
							width='24'
							height='24'
							className='w-[24px] h-[24px]'
						/>
					</div>
				</div>
			</div>
			<div className='flex gap-3 h-full'>
				{(() => {
					if (isLoading) {
						return (
							<div className='flex h-full w-full justify-center'>
								<Spinner />
							</div>
						)
					}

					if (!cohorts?.courses?.length) {
						return (
							<div className='flex flex-col w-full py-6 px-5 md:max-w-[240px] h-[224px] justify-between border border-dashed border-white/20 rounded-lg'>
								<Icon name='plus' width='16px' height='16px' />
								<div className='flex flex-col gap-2'>
									<p className='text-2xl text-zinc-50 font-bold leading-7'>
										Faça matrícula em um curso
									</p>
									<p className='text-sm text-zinc-50/70 leading-5'>
										Seus cursos em andamento aparecerão aqui
									</p>
								</div>
							</div>
						)
					}

					if (cohorts?.courses?.length) {
						// Ordena os cursos, separando os de progresso diferente de 100% primeiro
						const sortedCourses = [...cohorts.courses].sort((a, b) => {
							const progressA = parseFloat(a.progress.replace('%', ''))
							const progressB = parseFloat(b.progress.replace('%', ''))

							// Coloca os cursos com progresso menor que 100% antes dos que têm 100%
							if (progressA === 100 && progressB !== 100) return 1
							if (progressA !== 100 && progressB === 100) return -1
							return 0
						})

						return (
							<Swiper
								modules={[Pagination, Navigation]}
								slidesPerView='auto'
								spaceBetween={15}
								slidesPerGroup={1}
								centeredSlides={false}
								pagination={{
									clickable: false,
									dynamicBullets: false,
									el: null,
								}}
								autoHeight={true}
								watchSlidesProgress={true}
								slideToClickedSlide={true}
								grabCursor={true}
								breakpoints={{
									768: {
										slidesPerView:
											cohorts.courses.length > 2 ? 2 : cohorts.courses.length,
									},
									1024: {
										slidesPerView:
											cohorts.courses.length > 2 ? 3 : cohorts.courses.length,
									},
									1200: {
										slidesPerView:
											cohorts.courses.length > 4 ? 4 : cohorts.courses.length,
									},
								}}
								navigation={{
									nextEl: '.swiper-button-next-my-courses',
									prevEl: '.swiper-button-prev-my-courses',
								}}
								className='z-0 h-full min-h-[386px] w-full !overflow-visible'
							>
								{sortedCourses?.map((cohort, key) => (
									<SwiperSlide key={key} className='max-w-[320px]'>
										<CardCourse
											course={cohort}
											key={key}
											onClick={() => handleCohortOpen(cohort)}
										/>
									</SwiperSlide>
								))}
							</Swiper>
						)
					}

					return <></>
				})()}
			</div>
		</div>
	)
}
